import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Paper, Typography, Tabs, Tab, CircularProgress, 
    Button, Grid, Chip, Divider, IconButton, Card, CardContent 
} from '@mui/material';
import { 
    Edit, ArrowBack, Message, AttachFile, History, 
    CheckCircle, ErrorOutline, Schedule, Person 
} from '@mui/icons-material';
import api from '../../api/axios';

// Sub-components (will implement these in same file for now or separate if large)
import JobOverview from './tabs/JobOverview';
import JobTimeline from './tabs/JobTimeline';
import JobFiles from './tabs/JobFiles';
import JobAuditLog from './tabs/JobAuditLog';

const JobDetailView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadJob();
    }, [id]);

    const loadJob = async () => {
        try {
            const res = await api.get(`/admin/jobs/${id}`);
            setJob(res.data);
        } catch (err) {
            console.error(err);
            setError("Failed to load job details");
        }
        setLoading(false);
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Box sx={{ p: 4 }}><Typography color="error">{error}</Typography></Box>;
    if (!job) return <Box sx={{ p: 4 }}><Typography>Job not found</Typography></Box>;

    return (
        <Box sx={{ pb: 8 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/jobs')} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" fontWeight={700}>
                                {job.description}
                            </Typography>
                            <Chip 
                                label={job.status} 
                                size="small" 
                                sx={{ 
                                    fontWeight: 600,
                                    bgcolor: {
                                        'New Request': '#64748B', 'Needs Info': '#9CA3AF', 'Tentative': '#3B82F6',
                                        'Locked': '#EAB308', 'Confirmed': '#22C55E', 'Scheduled': '#3B82F6',
                                        'Failed': '#EF4444', 'Completed': '#8B5CF6', 'Waiting on Client': '#F97316',
                                        'En Route': '#06B6D4', 'On Site': '#4ADE80', 'Cancelled': '#9CA3AF'
                                    }[job.status] || '#9CA3AF',
                                    color: 'white'
                                }}
                            />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            ID: #{job.id} • {job.location}
                        </Typography>
                    </Box>
                </Box>
                <Button 
                    variant="outlined" 
                    startIcon={<Edit />}
                    onClick={() => navigate(`/jobs/${id}/edit`)} // We might change how edit works
                >
                    Edit Details
                </Button>
            </Box>

            {/* Tabs */}
            <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                    <Tab label="Overview" icon={<Person fontSize="small"/>} iconPosition="start" />
                    <Tab label="Communication" icon={<Message fontSize="small"/>} iconPosition="start" />
                    <Tab label="Files" icon={<AttachFile fontSize="small"/>} iconPosition="start" />
                    <Tab label="History" icon={<History fontSize="small"/>} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Content */}
            <Box>
                {tab === 0 && <JobOverview job={job} onRefresh={loadJob} />}
                {tab === 1 && <JobTimeline jobId={job.id} />}
                {tab === 2 && <JobFiles jobId={job.id} />}
                {tab === 3 && <JobAuditLog jobId={job.id} />}
            </Box>
        </Box>
    );
};

export default JobDetailView;
